import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Users, Calendar, Trash2, ChevronRight, Building2, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  getPartners, createPartner, updatePartner, deletePartner,
  getRetreats, createRetreat, deleteRetreat, fmt, fmtDate,
} from "@/lib/api";

const STATUS_STYLES = {
  planned: "bg-slate-50 text-slate-700 border-slate-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
};

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [retreats, setRetreats] = useState([]);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [retreatOpen, setRetreatOpen] = useState(false);
  const [retreatPartnerId, setRetreatPartnerId] = useState("");
  const [newPartner, setNewPartner] = useState({ name: "", contact: "" });
  const [newRetreat, setNewRetreat] = useState({
    name: "", start_date: "", end_date: "", participants: 0, status: "planned",
  });
  const [editPartner, setEditPartner] = useState(null); // {id, name, contact}
  const [confirmDelete, setConfirmDelete] = useState(null); // {kind:'partner'|'retreat', id, label, cascadeCount?}
  const [yearFilter, setYearFilter] = useState("all"); // "all" | "2025" | "2026" | "2027" | "future"

  const load = async () => {
    const [p, r] = await Promise.all([getPartners(), getRetreats()]);
    setPartners(p);
    setRetreats(r);
  };
  useEffect(() => { load(); }, []);

  const retreatsByPartner = useMemo(() => {
    const FIXED_YEARS = ["2025", "2026", "2027"];
    const inYear = (r) => {
      if (yearFilter === "all") return true;
      const y = (r.start_date || "").slice(0, 4);
      if (yearFilter === "future") return y && !FIXED_YEARS.includes(y);
      return y === yearFilter;
    };
    const filtered = retreats.filter(inYear);
    const g = {};
    for (const r of filtered) (g[r.partner_id] = g[r.partner_id] || []).push(r);
    return g;
  }, [retreats, yearFilter]);

  // Show all partners by default; when a year filter is active, hide partners
  // with no retreats in that year to keep the page focused.
  const visiblePartners = useMemo(() => {
    if (yearFilter === "all") return partners;
    return partners.filter(p => (retreatsByPartner[p.id] || []).length > 0);
  }, [partners, retreatsByPartner, yearFilter]);

  const yearCounts = useMemo(() => {
    const c = { "2025": 0, "2026": 0, "2027": 0, future: 0, all: retreats.length };
    for (const r of retreats) {
      const y = (r.start_date || "").slice(0, 4);
      if (y === "2025" || y === "2026" || y === "2027") c[y] += 1;
      else if (y) c.future += 1;
    }
    return c;
  }, [retreats]);

  const handleCreatePartner = async () => {
    if (!newPartner.name.trim()) return toast.error("Name required");
    try {
      await createPartner({ name: newPartner.name.trim(), contact: newPartner.contact.trim() });
      toast.success("Partner created");
      setPartnerOpen(false);
      setNewPartner({ name: "", contact: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create partner");
    }
  };

  const handleSaveEditPartner = async () => {
    if (!editPartner?.name?.trim()) return toast.error("Name required");
    try {
      await updatePartner(editPartner.id, {
        name: editPartner.name.trim(),
        contact: (editPartner.contact || "").trim(),
      });
      toast.success("Partner updated");
      setEditPartner(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update partner");
    }
  };

  const askDeletePartner = (p) => {
    const list = retreatsByPartner[p.id] || [];
    setConfirmDelete({
      kind: "partner",
      id: p.id,
      label: p.name,
      cascadeCount: list.length,
    });
  };

  const askDeleteRetreat = (r) => {
    setConfirmDelete({ kind: "retreat", id: r.id, label: r.name });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.kind === "partner") {
        await deletePartner(confirmDelete.id, (confirmDelete.cascadeCount || 0) > 0);
        toast.success("Partner removed");
      } else {
        await deleteRetreat(confirmDelete.id);
        toast.success("Retreat removed");
      }
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const openNewRetreat = (partnerId) => {
    setRetreatPartnerId(partnerId);
    const today = new Date().toISOString().slice(0, 10);
    const next = new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10);
    setNewRetreat({ name: "", start_date: today, end_date: next, participants: 0, status: "planned" });
    setRetreatOpen(true);
  };

  const handleCreateRetreat = async () => {
    if (!newRetreat.name.trim()) return toast.error("Retreat name required");
    if (newRetreat.end_date <= newRetreat.start_date) return toast.error("End date must be after start");
    try {
      await createRetreat({
        partner_id: retreatPartnerId,
        name: newRetreat.name.trim(),
        start_date: newRetreat.start_date,
        end_date: newRetreat.end_date,
        participants: parseInt(newRetreat.participants) || 0,
        status: newRetreat.status,
      });
      toast.success("Retreat created");
      setRetreatOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create retreat");
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Partners</h1>
          <p className="text-sm text-slate-500 mt-1">Retreat partners, scheduled retreats and financials</p>
        </div>
        <Button onClick={() => setPartnerOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white h-11" data-testid="partners-new-btn">
          <Plus size={16} /> New Partner
        </Button>
      </div>

      {/* Year tabs — filter retreats by start year. Counts are total across
          partners. "All" shows everything (default). */}
      {retreats.length > 0 && (
        <div className="flex items-center gap-1 border-b border-slate-200 -mb-px" data-testid="partners-year-tabs">
          {[
            { value: "all", label: "All" },
            { value: "2025", label: "2025" },
            { value: "2026", label: "2026" },
            { value: "2027", label: "2027" },
            { value: "future", label: "Future" },
          ].map(t => {
            const active = yearFilter === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setYearFilter(t.value)}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${active ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
                data-testid={`partners-year-tab-${t.value}`}
              >
                {t.label}
                <span className="ml-1.5 text-[11px] text-slate-400 tabular-nums">{yearCounts[t.value]}</span>
              </button>
            );
          })}
        </div>
      )}

      {partners.length === 0 ? (
        <Card className="border-slate-200 p-12 text-center text-slate-500" data-testid="partners-empty">
          <Building2 size={32} strokeWidth={1.5} className="mx-auto text-slate-300 mb-3" />
          <div className="font-heading font-semibold text-slate-700">No partners yet</div>
          <div className="text-xs mt-1">Create your first retreat partner to start tracking retreats and payments.</div>
        </Card>
      ) : visiblePartners.length === 0 ? (
        <Card className="border-slate-200 p-10 text-center text-slate-500" data-testid="partners-year-empty">
          <div className="font-heading font-semibold text-slate-700">No retreats in this year</div>
          <div className="text-xs mt-1">Switch to another year or &quot;All&quot; to see partners.</div>
        </Card>
      ) : (
        <div className="space-y-4">
          {visiblePartners.map(p => {
            const list = retreatsByPartner[p.id] || [];
            return (
              <Card key={p.id} className="border-slate-200 overflow-hidden shadow-none" data-testid={`partner-card-${p.id}`}>
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/40 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="font-heading font-semibold text-slate-900">{p.name}</h2>
                    {p.contact && <div className="text-xs text-slate-500 mt-0.5">{p.contact}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => openNewRetreat(p.id)} className="h-9" data-testid={`partner-add-retreat-${p.id}`}>
                      <Plus size={14} /> New retreat
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditPartner({ id: p.id, name: p.name, contact: p.contact || "" })}
                      className="h-9 w-9 p-0 text-slate-400 hover:text-slate-900"
                      title="Edit partner"
                      data-testid={`partner-edit-${p.id}`}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => askDeletePartner(p)}
                      className="h-9 w-9 p-0 text-slate-400 hover:text-red-600"
                      title="Delete partner"
                      data-testid={`partner-delete-${p.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {list.length === 0 ? (
                    <div className="px-5 py-6 text-sm text-slate-400 italic">No retreats yet</div>
                  ) : list.map(r => {
                    const s = r.summary || {};
                    return (
                      <div
                        key={r.id}
                        className="px-5 py-4 flex items-center gap-3 hover:bg-slate-50/50 transition-colors group"
                        data-testid={`retreat-row-${r.id}`}
                      >
                        <Link to={`/retreats/${r.id}`} className="flex-1 min-w-0 flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">{r.name}</div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                              <span className="flex items-center gap-1"><Users size={11} /> {r.participants}</span>
                              <Badge variant="outline" className={`${STATUS_STYLES[r.status] || STATUS_STYLES.planned} text-[10px] uppercase tracking-wider`}>{r.status?.replace("_", " ")}</Badge>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-heading font-bold text-slate-900">{fmt(s.total || 0)}</div>
                            <div className="text-[11px] text-slate-500">
                              <span className="text-emerald-700">{fmt(s.paid || 0)} paid</span>
                              {(s.remaining || 0) > 0.01 && <span className="text-amber-700 ml-2">{fmt(s.remaining || 0)} due</span>}
                            </div>
                          </div>
                          <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 shrink-0" />
                        </Link>
                        <button
                          onClick={() => askDeleteRetreat(r)}
                          className="text-slate-300 hover:text-red-600 p-2 shrink-0"
                          title="Delete retreat"
                          data-testid={`retreat-delete-${r.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create partner */}
      <Dialog open={partnerOpen} onOpenChange={(o) => !o && setPartnerOpen(false)}>
        <DialogContent className="max-w-md" data-testid="new-partner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Partner</DialogTitle>
            <DialogDescription className="sr-only">Create a new retreat partner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
              <Input value={newPartner.name} onChange={(e) => setNewPartner(v => ({ ...v, name: e.target.value }))} className="mt-1 h-11" placeholder="e.g. Yoga Tribe" autoFocus data-testid="new-partner-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Contact <span className="text-slate-300 normal-case tracking-normal">(email, phone)</span></Label>
              <Input value={newPartner.contact} onChange={(e) => setNewPartner(v => ({ ...v, contact: e.target.value }))} className="mt-1 h-11" placeholder="info@partner.com" data-testid="new-partner-contact" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartnerOpen(false)}>Cancel</Button>
            <Button onClick={handleCreatePartner} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-partner-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create retreat */}
      <Dialog open={retreatOpen} onOpenChange={(o) => !o && setRetreatOpen(false)}>
        <DialogContent className="max-w-md" data-testid="new-retreat-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Retreat</DialogTitle>
            <DialogDescription className="sr-only">Create a new retreat.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Retreat name</Label>
              <Input value={newRetreat.name} onChange={(e) => setNewRetreat(v => ({ ...v, name: e.target.value }))} className="mt-1 h-11" placeholder="Spring Yoga Retreat" autoFocus data-testid="new-retreat-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Start</Label>
                <Input type="date" value={newRetreat.start_date} onChange={(e) => setNewRetreat(v => ({ ...v, start_date: e.target.value }))} className="mt-1 h-11" data-testid="new-retreat-start" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">End</Label>
                <Input type="date" value={newRetreat.end_date} onChange={(e) => setNewRetreat(v => ({ ...v, end_date: e.target.value }))} className="mt-1 h-11" data-testid="new-retreat-end" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Participants</Label>
                <Input type="number" min="0" step="1" value={newRetreat.participants} onChange={(e) => setNewRetreat(v => ({ ...v, participants: e.target.value }))} className="mt-1 h-11" data-testid="new-retreat-participants" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
                <Select value={newRetreat.status} onValueChange={(v) => setNewRetreat(s => ({ ...s, status: v }))}>
                  <SelectTrigger className="mt-1 h-11" data-testid="new-retreat-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetreatOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateRetreat} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-retreat-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit partner */}
      <Dialog open={!!editPartner} onOpenChange={(o) => !o && setEditPartner(null)}>
        <DialogContent className="max-w-md" data-testid="edit-partner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Partner</DialogTitle>
            <DialogDescription className="sr-only">Update partner details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
              <Input
                value={editPartner?.name || ""}
                onChange={(e) => setEditPartner(v => ({ ...v, name: e.target.value }))}
                className="mt-1 h-11"
                autoFocus
                data-testid="edit-partner-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Contact <span className="text-slate-300 normal-case tracking-normal">(email, phone)</span></Label>
              <Input
                value={editPartner?.contact || ""}
                onChange={(e) => setEditPartner(v => ({ ...v, contact: e.target.value }))}
                className="mt-1 h-11"
                data-testid="edit-partner-contact"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPartner(null)}>Cancel</Button>
            <Button onClick={handleSaveEditPartner} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-partner-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {confirmDelete?.kind === "partner" ? "Delete partner?" : "Delete retreat?"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              {confirmDelete?.kind === "partner" ? (
                <>
                  This will permanently remove <span className="font-semibold text-slate-900">{confirmDelete?.label}</span>
                  {(confirmDelete?.cascadeCount || 0) > 0 ? (
                    <> and <span className="font-semibold text-red-700">{confirmDelete.cascadeCount} retreat{confirmDelete.cascadeCount === 1 ? "" : "s"}</span> with all their costs and payments.</>
                  ) : (
                    <>.</>
                  )}
                  {" "}This cannot be undone.
                </>
              ) : (
                <>
                  This will permanently remove <span className="font-semibold text-slate-900">{confirmDelete?.label}</span> with all its costs and payments. This cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} data-testid="confirm-delete-cancel">Cancel</Button>
            <Button onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-confirm">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
